// Trips saved to the guest's account, on the website.
//
// The planner (packages/discover) keeps working on the browser's copy of the
// trips, exactly as it always has. This component is what makes that copy the
// account's while a guest is signed in. The steps and the merge rules are
// shared with the app (packages/discover/src/lib/trip-account-sync.ts); this
// file only plugs in localStorage, the /me/trips client and the question.
//
// - On sign-in the account's trips are pulled and merged with the browser's.
// - Every change the planner writes is pushed shortly after.
// - Trips made while signed out are offered to the account ("Keep them" /
//   "Not now"), never merged silently. "Not now" leaves them on this browser,
//   visible and editable, just not on the account, and the question is not
//   asked again until the guest signs out.
// - On sign-out the account's trips leave the browser and the guest's own
//   stay, so a shared computer does not show one person's trips to the next.
//
// The account's list is read through the same React Query entry the account
// page shows (accountKeys.trips), so a page load asks the server once.
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { PW, pewterPanelApproved, pewterPrimary } from "@majourneys/booking";
import {
  EMPTY_TRIP_ACCOUNT_STATE,
  TRIPS_CHANGED_EVENT,
  createTripAccountSync,
  migrateTrip,
  parseTripAccountState,
  readTrips,
  writeTrips,
  type Trip,
  type TripAccountApi,
  type TripAccountState,
  type TripsChangedDetail,
  // The pure subpath: the main entry would pull the whole Discover app, its
  // maps and the trip planner into every page's first download.
} from "@majourneys/discover/trips";
import type { SavedTripDto } from "@majourneys/shared";
import { useSession } from "../../../lib/auth";
import { tripsApi } from "../api/trips.api";
import { accountKeys } from "../constants/query-keys";

export const TRIP_ACCOUNT_KEY = "mj-trips-account:v1";
export const GUEST_TRIPS_STASH_KEY = "mj-trips-guest-stash:v1";
/** The origin this component writes trips with, so it ignores its own writes. */
const ORIGIN = "account-sync";
/** How long after the last edit the change is sent. */
const PUSH_DELAY_MS = 1200;
/** At most one refresh from the account per this long when the tab comes back. */
const FOCUS_REFRESH_MS = 60_000;
/**
 * A list fetched this recently is reused rather than asked for again: the
 * account page and this component both want it the moment a page loads.
 */
const LIST_FRESH_MS = 5_000;

function readState(): TripAccountState {
  try {
    const raw = window.localStorage.getItem(TRIP_ACCOUNT_KEY);
    if (!raw) return EMPTY_TRIP_ACCOUNT_STATE;
    return parseTripAccountState(JSON.parse(raw));
  } catch {
    return EMPTY_TRIP_ACCOUNT_STATE;
  }
}

/**
 * /me/trips for the sync, sharing the account page's React Query entry.
 *
 * The list joins a request already in flight (or one a moment old) instead of
 * sending its own. Writes only mark that entry out of date; `settle()` then
 * refetches it once, after a whole sync run, and only if something was written.
 */
export function tripAccountQueryApi(
  queryClient: QueryClient,
  client: Pick<typeof tripsApi, "listMine" | "save" | "remove" | "sync"> = tripsApi,
): TripAccountApi & { settle(): void } {
  let dirty = false;
  const markStale = () => {
    dirty = true;
    void queryClient.invalidateQueries({ queryKey: accountKeys.trips(), refetchType: "none" });
  };
  return {
    list: async () => {
      const docs = await queryClient.fetchQuery<SavedTripDto[]>({
        queryKey: accountKeys.trips(),
        queryFn: () => client.listMine(),
        staleTime: LIST_FRESH_MS,
      });
      return docs.map((d) => d.trip as unknown as Trip);
    },
    save: async (trip) => {
      await client.save(trip);
      markStale();
    },
    remove: async (id) => {
      await client.remove(id);
      markStale();
    },
    sync: async (trips) => {
      await client.sync(trips);
      markStale();
    },
    settle: () => {
      if (!dirty) return;
      dirty = false;
      void queryClient.invalidateQueries({ queryKey: accountKeys.trips() });
    },
  };
}

function writeState(state: TripAccountState): void {
  try {
    if (state.owner === null) window.localStorage.removeItem(TRIP_ACCOUNT_KEY);
    else window.localStorage.setItem(TRIP_ACCOUNT_KEY, JSON.stringify(state));
  } catch {
    /* Storage blocked: the next visit starts the merge again, which is safe. */
  }
}

function readStash(): Trip[] {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(GUEST_TRIPS_STASH_KEY) ?? "[]");
    return Array.isArray(parsed) ? (parsed as Trip[]).map(migrateTrip) : [];
  } catch {
    return [];
  }
}

function writeStash(trips: readonly Trip[]): void {
  try {
    if (trips.length === 0) window.localStorage.removeItem(GUEST_TRIPS_STASH_KEY);
    else window.localStorage.setItem(GUEST_TRIPS_STASH_KEY, JSON.stringify(trips));
  } catch {
    /* Storage blocked: nothing to keep them in. */
  }
}

export function TripsAccountSync() {
  const { t } = useTranslation();
  const session = useSession() as {
    data?: { user?: { id?: string } } | null;
    isPending?: boolean;
  };
  const userId = session.data?.user?.id ?? null;
  const pending = session.isPending ?? false;
  const queryClient = useQueryClient();
  const [offer, setOffer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const { sync, settle } = useMemo(() => {
    const api = tripAccountQueryApi(queryClient);
    return {
      settle: api.settle,
      sync: createTripAccountSync(api, {
        readTrips,
        writeTrips: (trips) => writeTrips(trips, ORIGIN),
        readState,
        writeState,
        readStash,
        writeStash,
      }),
    };
  }, [queryClient]);

  // Sign-in, sign-out, and a returning signed-in visit.
  useEffect(() => {
    if (pending || typeof window === "undefined") return;
    let cancelled = false;
    if (!userId) {
      setOffer(false);
      void sync.signedOut().catch(() => undefined);
      return;
    }
    sync
      .signedIn(userId)
      .then((outcome) => {
        if (cancelled) return;
        setOffer(outcome.kind === "offer");
      })
      .catch(() => {
        /* Offline or the API is down: the next visit tries again. */
      })
      .finally(settle);
    return () => {
      cancelled = true;
    };
  }, [userId, pending, sync, settle]);

  // Push the planner's edits as they happen (not while the question is up:
  // until the guest answers, the browser's trips are not the account's).
  useEffect(() => {
    if (!userId || offer || typeof window === "undefined") return;
    let timer: number | undefined;
    const onChanged = (e: Event) => {
      if ((e as CustomEvent<TripsChangedDetail>).detail?.origin === ORIGIN) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void sync
          .push(userId)
          .catch(() => undefined)
          .finally(settle);
      }, PUSH_DELAY_MS);
    };
    window.addEventListener(TRIPS_CHANGED_EVENT, onChanged);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener(TRIPS_CHANGED_EVENT, onChanged);
    };
  }, [userId, offer, sync, settle]);

  // Coming back to the tab picks up what was changed on the phone meanwhile
  // (the pull refreshes the account page's list as it reads it).
  useEffect(() => {
    if (!userId || offer || typeof document === "undefined") return;
    let last = Date.now();
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - last < FOCUS_REFRESH_MS) return;
      last = Date.now();
      void sync
        .pull(userId)
        .catch(() => undefined)
        .finally(settle);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [userId, offer, sync, settle]);

  async function answer(keep: boolean) {
    if (!userId) return;
    setBusy(true);
    setFailed(false);
    try {
      if (keep) await sync.keep(userId);
      else await sync.notNow(userId);
      setOffer(false);
    } catch {
      setFailed(true);
    } finally {
      settle();
      setBusy(false);
    }
  }

  if (!offer || !userId) return null;
  const panel = pewterPanelApproved(24);
  return (
    // Below lg the phone bottom bar (68px row + max(12px, safe area)) sits at
    // the foot of every page; the card clears it instead of covering it.
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[var(--z-toast,60)] flex justify-center px-4 max-lg:bottom-[calc(80px_+_max(12px,env(safe-area-inset-bottom)))]">
      <div
        role="dialog"
        aria-labelledby="mj-trips-offer-title"
        aria-describedby="mj-trips-offer-body"
        className="pointer-events-auto w-full min-w-0 max-w-[440px] p-4"
        style={{ ...panel, boxShadow: `${panel.boxShadow}, 0 18px 40px -20px rgba(17,14,57,0.45)` }}
      >
        <p id="mj-trips-offer-title" className="m-0 text-[16px] font-bold leading-snug" style={{ color: PW.navy }}>
          {t("account.trips.offer_title")}
        </p>
        <p id="mj-trips-offer-body" className="m-0 mt-1 text-[14px] font-medium leading-snug" style={{ color: PW.muted }}>
          {failed ? t("account.trips.offer_error") : t("account.trips.offer_body")}
        </p>
        <div className="mt-3 flex min-w-0 gap-2">
          <button
            type="button"
            onClick={() => void answer(false)}
            disabled={busy}
            className="mj-press min-h-[44px] min-w-0 flex-1 rounded-full px-4 text-[14px] font-semibold disabled:opacity-60"
            style={{ color: PW.navy, background: PW.controlFill, boxShadow: `inset 0 0 0 1px ${PW.controlEdge}` }}
          >
            {t("account.trips.offer_not_now")}
          </button>
          <button
            type="button"
            onClick={() => void answer(true)}
            disabled={busy}
            className="mj-press min-h-[44px] min-w-0 flex-1 rounded-full px-4 text-[14px] font-semibold text-white disabled:opacity-60"
            style={pewterPrimary}
          >
            {t("account.trips.offer_keep")}
          </button>
        </div>
      </div>
    </div>
  );
}
